import React, { Fragment, memo, useState, useEffect, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Checkbox } from 'primereact/checkbox';
import { DATE_FORMAT, EVENT_TYPE, CORE_STATUSES, FILE_DOWNLOAD_API_PATH, SortColumnsType } from '@Constants/constants';
import Moment from 'moment';
import { Paginator } from 'primereact/paginator';
import { getDataTableSortFields, getSearchParamSortFields } from '@Helpers/sortHelper';
import { MultiSelect } from 'primereact/multiselect';
import { classNames } from 'primereact/utils';
import MultiSelectBox from '@Common/MultiSelectBox/MultiSelectBox';
import Notification from '@Common/Notification';
import BulkUpdateResponsibleUserDrawer from '../../BulkUpdateResponsibleUserDrawer';
import Button from '@Common/Button';
import Modal from '@Common/Modal/Modal';
import Icon from '@Common/Icon';
import Tag from '@Common/Tag/Tag';
import DateRangePickerWrapper from '@Common/DateRangePicker/DateRangePickerWrapper';
import { getTaskStatusClassName } from '@Helpers/statusColors';
import { TruncateText } from '@Helpers/strings';
import { daysToDaysWeeks } from '@Helpers/dateTime';
import LoadingIndicator from '@Common/LoadingIndicator/LoadingIndicator';
import { NavigateToRouteWithDashboardParams } from '@Helpers/routing';
import { REPORT_IDS } from '@Constants/constants';
import { Routes } from '@Constants/routes';
import { CUSTOMER_ROLES } from '@Constants/roles';
import { getMultiSelectFilterValues } from '@Helpers/multiselect';
import { isAllLocationsMode } from '@Helpers/taskListHelpers';
import TaskCompressionModal from './TaskCompressionModal/TaskCompressionModal';
import './ListView.scss';

interface ListViewProps {
	onApplySearchParams: (params: any) => any;
	assignTaskToMe: (taskId: number) => void;
	changeTaskStatus: (taskId: number, statusId: number) => void;
	saveColumnOrder: (request: any) => void;
	downloadFile: (fileName: string) => void;
	responsibleUsers?: any[];
	isLoading: boolean;
	locationTasks: any[];
	totalCount: number;
	filters: any;
	columnFilters: any;
	searchParams: any;
	allTasksChecked: boolean;
	onSort: (sortBy: string, sortDir: string) => void;
	updateSearchParams: (key: string | null, value: unknown, search?: boolean) => void;
	clearAllFilters: () => void;
	onMasterCheckboxToggle: () => any;
	onToggleCheckedTask: (id: number) => any;
	showTask: (id: number, isBaseline?: boolean) => void;
	bulkUpdateMode: boolean;
	userId: number;
	bulkUpdateStatus: () => void;
	bulkUpdateResponsibleUser: () => void;
	bulkUpdateInactive: () => void;
	bulkUpdateFollowTask: (selectedBulkValue: string) => void;
	bulkShiftDates: (selectedBulkValue: string) => void;
	bulkUpdateByJobTitle: () => void;
	onColumnsClick: () => void;
	userRole: string;
	onGanttExportToPDF?: () => void;
	checkedTasks?: any[];
}

interface DateRangeState {
	start: any;
	end: any;
	name: string;
}

export const ListView = (props: ListViewProps) => {
	const [cols, setCols] = useState<any[]>([]);
	const [selectedTask, setSelectedTask] = useState<any>(null);
	const [isUpdateResponsibleUserDrawerOpen, setIsUpdateResponsibleUserDrawerOpen] = useState(false);
	const [showDateRangePicker, setShowDateRangePicker] = useState(false);
	const [selectedBulkValue, setSelectedBulkValue] = useState('');
	const [dateRange, setDateRange] = useState<DateRangeState>({
		start: null,
		end: null,
		name: '',
	});
	const checkedTaskCount = props.checkedTasks?.length ?? 0;
	const [dropFilterChanged, setDropFilterChanged] = useState(false);
	const [isCompressionModalOpen, setIsCompressionModalOpen] = useState(false);
	const selectedTaskCount = props.checkedTasks?.length ?? 0;

	// Mirror props/selectedTask through refs so callbacks captured into `cols`
	// state always read the latest values (matching class `this.props`/`this.state`).
	const propsRef = useRef(props);
	propsRef.current = props;
	const selectedTaskRef = useRef<any>(selectedTask);
	selectedTaskRef.current = selectedTask;

	const onSort = ({ sortField, sortOrder }: any) => {
		const { sortBy, sortDir } = getSearchParamSortFields(sortField, sortOrder);
		props.onSort(sortBy, sortDir);
	};

	const onUpdateUserComplete = () => {
		props.updateSearchParams(null, null, true);
		closeUpdateUserDrawer();
	};

	const closeUpdateUserDrawer = () => {
		setIsUpdateResponsibleUserDrawerOpen(false);
	};

	const onDataTableRowClick = (e: any) => {
		const target = e.originalEvent.target;
		const { className = '', innerHTML = '', localName = '', classList } = target;
		const rowData = e.data;

		// Fix: Ensure className is always a string
		const classNameStr = typeof className === 'string' ? className : (className?.toString() || '');
		const isCheckboxElement = classNameStr.includes('p-checkbox') || innerHTML.includes('p-checkbox-box') || classList.contains('p-checkbox-icon');
		const isResponsibleUserLink = classNameStr.includes('text-primary');
		const isResponsibleUser = classNameStr.includes('responsible-user');
		const isChildElement = localName !== 'td';

		setSelectedTask(rowData);

		// Early returns for better performance
		if (isResponsibleUser || isChildElement) {
			return; // DO NOTHING if they're targeting the select box in the status column
		}

		if (isResponsibleUserLink) {
			setIsUpdateResponsibleUserDrawerOpen(true);
			return;
		}

		if (isCheckboxElement) {
			onToggleCheckedTask(rowData);
			return;
		}

		// Default action - show task
		props.showTask(rowData.id, rowData.baselineTaskId === 0 || rowData.baselineTaskId === null ? false : true);
	};

	const milestoneTemplate = (rowData: any) => {
		return (
			<div className='milestone-row'>
				{rowData.isMilestone && (
					<div className='text-warning' title='Milestone Task'>
						<i className='fa-regular fa-diamonds-4'></i>
					</div>
				)}
				{rowData.links && rowData.links.length > 0 && (
					<div title='Has Links'>
						<Icon name='link' />
					</div>
				)}
				{rowData.hasFiles && (
					<div title='Has Files'>
						<i className='fa-regular fa-file'></i>
					</div>
				)}
				{rowData.lastComment && (
					<div title='Has Comments'>
						<i className='fa-regular fa-comment-dots'></i>
					</div>
				)}
				{rowData.isFollowedByCurrentUser && (
					<div title='Following task'>
						<Icon name='eye' />
					</div>
				)}
			</div>
		);
	};

	const colorColumnTemplate = (rowData: any) => {
		const coreStatus = rowData.coreStatusId;
		const userDate = Moment.utc(rowData.userDate);
		const targetDate = Moment.utc(rowData.targetDate);
		const startDate = rowData.startDate ? Moment.utc(rowData.startDate) : null; // startDate can be null
		const classCode = getTaskStatusClassName(coreStatus, userDate, targetDate, startDate);

		return <div className={classCode} style={{ width: '5px' }}></div>;
	};

	const onNameClick = (rowData: any) => {
		propsRef.current.showTask(rowData.id, rowData.baselineTaskId === 0 || rowData.baselineTaskId === null ? false : true);
	};

	const nameTemplate = (rowData: any) => {
		return (
			<span className='task-name show-newlines task-description-column' onClick={() => onNameClick(rowData)}>
				{rowData.name}
			</span>
		);
	};

	const additionalInfoTemplate = (rowData: any) => {
		return <span className='show-newlines'>{TruncateText(rowData.additionalInfo, 200)}</span>;
	};

	const prereqsTemplate = (rowData: any) => {
		if (rowData.prereqs && rowData.prereqs.length > 0) {
			const prereqs = rowData.prereqs.length;
			const completed = rowData.prereqs.filter((m: any) => m.coreStatusName === 'Complete').length;
			return completed + '/' + prereqs;
		} else {
			return 'N/A';
		}
	};

	const stepTemplate = (rowData: any) => {
		if (rowData.steps && rowData.steps.length > 0) {
			const activeSteps = rowData.steps.filter((m: any) => m.isActive);
			const completed = activeSteps.filter((m: any) => m.isCompleted);
			const stepsCompleted = completed ? completed.length : 0;

			return `${stepsCompleted}/${activeSteps.length} done`;
		} else {
			return 'N/A';
		}
	};

	const linkTemplate = (rowData: any) => {
		let retval: React.ReactNode[] = [];

		if (rowData.links && rowData.links.length > 0) {
			for (let i = 0; i < rowData.links.length; i++) {
				const link = rowData.links[i];

				// Check if URL contains download API endpoint to determine if it's a file
				const isFileDownload = link.url && link.url.includes(FILE_DOWNLOAD_API_PATH);

				// Create a unique key using both the link ordinal and array index
				const uniqueKey = `${rowData.id}-link-${link.ordinal || 'no-ordinal'}-${i}`;

				if (isFileDownload) {
					// This is a file - use download API call
					retval.push(
						<button key={uniqueKey} type='button' className='btn btn-link' onClick={(e) => onDownloadLink(link, e)}>
							{link.name}
						</button>
					);
				} else if (link.url) {
					// This is an external URL - open in new tab
					// Ensure the URL has a protocol, otherwise browser treats it as relative
					const fullUrl = link.url.startsWith('http://') || link.url.startsWith('https://')
						? link.url
						: `https://${link.url}`;

					retval.push(
						<div key={uniqueKey}>
							<a href={fullUrl} rel='noopener noreferrer' target='_blank'>
								{link.name}
							</a>
						</div>
					);
				} else {
					// No URL available
					retval.push(
						<span key={uniqueKey}>
							{link.name}
						</span>
					);
				}
			}
		}

		return retval;
	};

	const onDownloadLink = (link: any, e: React.MouseEvent) => {
		e.preventDefault();
		// extract the file name from the link.url
		const start = link.url.indexOf('=') + 1;
		const fileName = link.url.slice(start);
		propsRef.current.downloadFile(fileName);
	};

	const contactInfoTemplate = (rowData: any) => {
		return (
			<div className='contact-info'>
				{rowData.contactName ? <div>{rowData.contactName}</div> : ''}
				{rowData.contactEmail ? <div>{rowData.contactEmail}</div> : ''}
			</div>
		);
	};

	const responsibleUserTemplate = (rowData: any) => {
		let name: React.ReactNode = '';
		const assignToMe =
			rowData.responsibleUserId != propsRef.current.userId ? (
				<div>
					<span className='responsible-user-assign' onClick={() => onAssignToMe(rowData.id)}>
						Assign Task to Me
					</span>
				</div>
			) : (
				''
			);
		if (rowData.responsibleUserId) {
			name = (
				<div>
					<div>{rowData.responsibleUserName}</div>
					<div className='responsible-user-jobtitle'>{rowData.responsibleUserJobTitle}</div>
				</div>
			);
		} else {
			name = <div>{rowData.jobTitleName}</div>;
		}

		return (
			<div className='responsible-user'>
				{name}
				{assignToMe}
			</div>
		);
	};

	const baselineTemplate = (rowData: any) => {
		return rowData.baselineName ? rowData.baselineName : '';
	};

	const phaseTemplate = (rowData: any) => {
		return rowData.phaseName ? rowData.phaseName : '';
	};

	const communityTemplate = (rowData: any) => {
		return rowData.communityName ? rowData.communityName : '';
	};

	const locationTemplate = (rowData: any) => {
		return rowData.locationName ? rowData.locationName : '';
	};

	const projectTemplate = (rowData: any) => {
		const hasAnyRowData =
			rowData.isMilestone || rowData.links?.length > 0 || rowData.hasFiles || rowData.lastComment || rowData.isFollowedByCurrentUser;

		return (
			<div className='project-row'>
				{hasAnyRowData && <span className='ms-2'>{milestoneTemplate(rowData)}</span>}
				<span className={`${!hasAnyRowData ? 'project-name' : ''}`}>{rowData.projectName || ''}</span>
			</div>
		);
	};

	const departmentTemplate = (rowData: any) => {
		return rowData.departmentName ? rowData.departmentName : '';
	};

	const modifiedDateTemplate = (rowData: any) => {
		return rowData.lastModifiedDate ? Moment.utc(rowData.lastModifiedDate).format(DATE_FORMAT) : '';
	};

	const createdDateTemplate = (rowData: any) => {
		return Moment.utc(rowData.createdDate).format(DATE_FORMAT);
	};

	const startDateTemplate = (rowData: any) => {
		if (rowData.startDate && rowData.startDate.trim().length > 0) {
			return Moment.utc(rowData.startDate).format(DATE_FORMAT);
		} else {
			return '';
		}
	};

	const statusTemplate = (rowData: any) => {
		const statusId = rowData.statusId ? rowData.statusId.toString() : '';
		return (
			<div className='select-column-header'>
				<MultiSelectBox
					name='statusId'
					// label='Status'
					className='mb-0'
					placeholder='Status'
					idField='id'
					valueField='name'
					options={propsRef.current.columnFilters.statuses}
					selectedValues={statusId}
					onChange={onChangeStatus}
					isMulti={false}
				/>
			</div>
		);
	};

	const onAssignToMe = (taskId: number) => {
		// updates the task when they click the Assign to Me in the Responsible User column
		propsRef.current.assignTaskToMe(taskId);
	};

	const onChangeStatus = (statusId: any) => {
		propsRef.current.changeTaskStatus(selectedTaskRef.current.id, statusId);
	};

	const targetDateTemplate = (rowData: any) => {
		const targetDate = Moment.utc(rowData.targetDate);
		const targetDateFormatted = targetDate.format(DATE_FORMAT);

		if (rowData.statusName === 'Complete' || rowData.statusName === 'Not Applicable') {
			return <div className='text-success'>{targetDateFormatted}</div>;
		} else {
			if (Moment().isAfter(targetDate.add(1, 'days'))) {
				return <div className='text-danger'>{targetDateFormatted}</div>;
			} else {
				return targetDateFormatted;
			}
		}
	};

	const checkboxTemplate = (rowData: any) => {
		return (
			<Checkbox
				name='checkbox'
				onChange={() => onToggleCheckedTask(rowData)}
				checked={rowData.isChecked}
			/>
		);
	};

	const lastCommentTemplate = (rowData: any) => {
		if (rowData.lastComment) {
			const comment = TruncateText(rowData.lastComment.comment, 200);
			return (
				<div>
					<span className='comment-rendered text-break' dangerouslySetInnerHTML={{ __html: comment }}></span>
					<div className='text-muted'>
						{rowData.lastComment.createdBy} @ {Moment.utc(rowData.lastComment.createdDate).format(DATE_FORMAT)}
					</div>
				</div>
			);
		} else {
			return '';
		}
	};

	const tagsTemplate = (rowData: any) => {
		const retval: React.ReactNode[] = [];

		if (rowData.tags && rowData.tags.length > 0) {
			for (let i = 0; i < rowData.tags.length; i++) {
				const tag = rowData.tags[i];

				// Create a unique key using rowData.id, tag name, and index
				const uniqueKey = `${rowData.id}-tag-${tag.name}-${i}`;

				retval.push(
					<div className='mb-1' key={uniqueKey}>
						<Tag title={tag.name} color={tag.fontColor} backgroundColor={tag.backgroundColor} />
					</div>
				);
			}
		}
		return retval;
	};

	const onToggleCheckedTask = (task: any) => {
		props.onToggleCheckedTask(task.id);
	};

	const onMasterCheckboxToggle = () => {
		props.onMasterCheckboxToggle();
	};

	const isColumnFeatureEnabled = (fieldName: string) => {
		// determines if the columns should be shown based on special rules
		switch (fieldName) {
			case 'projectName':
				return propsRef.current.filters.isProjectWidgetActive;
			case 'startDate':
				return propsRef.current.filters.isStartDateActive;
			case 'steps':
				return propsRef.current.filters.isTaskStepsActive;
			case 'prereqs':
				return propsRef.current.filters.isTaskDependenciesActive;
			case 'locationName':
				/**
				 * 1320: Only show the locationName column when more than one
				 * location has been selected in the header's location selector.
				 * "All locations" mode selects no location keys at all, but the
				 * results still span every accessible location, so the column is
				 * needed there too.
				 */
				return (
					isAllLocationsMode(propsRef.current.searchParams) ||
					(propsRef.current.searchParams.locationKeys && propsRef.current.searchParams.locationKeys.length > 1)
				);
			default:
				return true;
		}
	};

	const setColumnTemplate = (column: any) => {
		switch (column.field) {
			case 'name':
				column.body = nameTemplate;
				break;
			case 'additionalInfo':
				column.body = additionalInfoTemplate;
				break;
			case 'projectName':
				column.body = projectTemplate;
				// 1512: Shrink Project to make more room for Task
				column.style = { width: '140px' };
				break;
			case 'startDate':
				column.body = startDateTemplate;
				column.style = { width: '95px' };
				break;
			case 'targetDate':
				column.body = targetDateTemplate;
				column.style = { width: '105px' };
				break;
			case 'responsibleUserName':
				column.body = responsibleUserTemplate;
				column.style = { width: '140px' }; // Explicitly set Responsible User column width so header doesn't wrap when bulk mode enabled
				break;
			case 'departmentName':
				column.body = departmentTemplate;
				// 1512: Shrink Dept to make more room for Task
				column.style = { width: '140px' };
				break;
			case 'links':
				column.body = linkTemplate;
				break;
			case 'steps':
				column.body = stepTemplate;
				column.style = { width: '76px' };
				break;
			case 'prereqs':
				column.body = prereqsTemplate;
				break;
			case 'locationName':
				column.body = locationTemplate;
				break;
			case 'createdDate':
				column.body = createdDateTemplate;
				column.style = { width: '108px' };
				break;
			case 'lastModifiedDate':
				column.body = modifiedDateTemplate;
				column.style = { width: '112px' };
				break;
			case 'contactInfo':
				column.body = contactInfoTemplate;
				break;
			case 'communityName':
				column.body = communityTemplate;
				break;
			case 'phaseName':
				column.body = phaseTemplate;
				break;
			case 'baselineName':
				column.body = baselineTemplate;
				break;
			case 'statusName':
				column.body = statusTemplate;
				column.style = { width: '150px' };
				break;
			case 'id':
				column.style = { width: '76px' };
				break;
			case 'baselineTaskId':
				column.style = { width: '76px' };
				break;
			case 'targetWeeksDays':
				column.body = (rowData: any) => {
					return daysToDaysWeeks(rowData.targetDaysOut);
				};
				column.style = { width: '76px' };
				break;
			case 'discipline':
				column.style = { width: '76px' };
				break;
			case 'lastComment':
				column.body = lastCommentTemplate;
				column.style = { maxWidth: '25%' };
				break;
			case 'tags':
				column.body = tagsTemplate;
				break;
		}
	};

	const buildColumnOptions = () => {
		// Define system columns once to avoid repeated allocations
		const systemColumns = ['coreStatusId', 'isChecked'];

		// Helper function to build column objects consistently
		const buildColumn = (savedColumn: any, masterColumn: any) => ({
			field: savedColumn.fieldName,
			header: savedColumn.displayName,
			isSortable: masterColumn.isSortable,
		});

		let columns: any[] = [];

		// Check if we have saved columns from the backend
		if (propsRef.current.filters.savedColumns && propsRef.current.filters.savedColumns.length > 0) {
			// Filter out system columns from saved columns immediately to ensure consistent behavior
			let remainingColumns = propsRef.current.filters.savedColumns.filter(
				(col: any) => !systemColumns.includes(col.fieldName)
			);

			// Start with an empty array to build the columns in the correct order
			let orderedColumns: any[] = [];

			/**
			 * Use a Set to track processed field names for O(1) lookup performance.  It
			 * also keeps a field that is saved more than once (locationName is force-added
			 * on load, so it can end up saved twice) from rendering as a duplicate column.
			 */
			const processedFieldNames = new Set();

			// If we have a saved column order, use it to determine the sequence
			if (propsRef.current.filters.savedColumnOrder && propsRef.current.filters.savedColumnOrder.length > 0) {
				// First, add columns in the order specified by savedColumnOrder
				for (let i = 0; i < propsRef.current.filters.savedColumnOrder.length; i++) {
					const fieldName = propsRef.current.filters.savedColumnOrder[i];

					// Skip system columns - they're handled separately in JSX
					if (systemColumns.includes(fieldName) || processedFieldNames.has(fieldName)) {
						continue;
					}

					// Find this column in savedColumns
					const savedColumn = propsRef.current.filters.savedColumns.find((col: any) => col.fieldName === fieldName);
					if (savedColumn) {
						// Find the master column for additional configuration
						const masterColumn = propsRef.current.filters.masterColumns.find((col: any) => col.fieldName === fieldName);

						if (masterColumn) {
							let column = buildColumn(savedColumn, masterColumn);
							setColumnTemplate(column);

							// Track this column as processed regardless of feature enablement
							// to prevent reprocessing in the remaining columns loop
							processedFieldNames.add(fieldName);

							if (isColumnFeatureEnabled(column.field)) {
								orderedColumns.push(column);
							}
						}
					}
				}

				// Filter remaining columns once after processing ordered columns
				remainingColumns = remainingColumns.filter((col: any) => !processedFieldNames.has(col.fieldName));
			}

			// Add any remaining columns that weren't in savedColumnOrder (newly selected columns)
			for (let i = 0; i < remainingColumns.length; i++) {
				const savedColumn = remainingColumns[i];

				if (processedFieldNames.has(savedColumn.fieldName)) {
					continue;
				}

				// Find the corresponding master column for configuration
				const masterColumn = propsRef.current.filters.masterColumns.find((col: any) => col.fieldName === savedColumn.fieldName);

				if (masterColumn) {
					let column = buildColumn(savedColumn, masterColumn);
					setColumnTemplate(column);
					processedFieldNames.add(savedColumn.fieldName);

					if (isColumnFeatureEnabled(column.field)) {
						orderedColumns.push(column);
					}
				}
			}

			columns = orderedColumns;
		} else {
			// Fallback to default columns when no saved columns exist
			for (let i = 0; i < propsRef.current.filters.masterColumns.length; i++) {
				const masterColumn = propsRef.current.filters.masterColumns[i];

				let column = {
					field: masterColumn.fieldName,
					header: masterColumn.displayName,
					isSortable: masterColumn.isSortable,
				};

				setColumnTemplate(column);
				if (masterColumn.isDefault && isColumnFeatureEnabled(column.field)) {
					columns.push(column);
				}
			}
		}

		setCols(columns);
	};

	const onTextFilterChange = (e: any) => {
		props.updateSearchParams(e.target.name, e.target.value, false);
	};

	const onSearchBlur = () => {
		props.updateSearchParams(null, null, true);
	};

	const keyUpFilter = (e: any) => {
		if (e.keyCode == 13) {
			props.updateSearchParams(null, null, true);
		}
	};

	const onPageChange = (e: any) => {
		props.updateSearchParams('pageNumber', e.page + 1, true);
	};

	const onTriggerSearchFromCustomComponent = (clicked: boolean) => {
		if (clicked) {
			props.updateSearchParams(null, null, true);
		}
	};

	const onBulkMultiSelectChange = (value: string) => {
		if (['FollowTask', 'UnfollowTask', 'Inactive', 'ShiftDates'].includes(value) && checkedTaskCount === 0) {
			Notification('error', 'No tasks selected', 'Please select at least one task to apply this action.');
			return;
		}

		setSelectedBulkValue(value);
		switch (value) {
			case 'ChangeStatus':
				props.bulkUpdateStatus?.();
				resetBulkMultiSelectValue();
				break;
			case 'ChangeResponsible':
				props.bulkUpdateResponsibleUser?.();
				resetBulkMultiSelectValue();
				break;
			default:
				break;
		}
	};

	const resetBulkMultiSelectValue = () => {
		setSelectedBulkValue('');
	};

	// remember which date range we are working with and show the modal
	// also populate any existing searchParams values for that particular range
	const openDateRangePicker = (rangeName: string) => {
		let start: any = null;
		let end: any = null;

		switch (rangeName) {
			case 'startDate':
				start = props.searchParams.startDateStart ? props.searchParams.startDateStart : null;
				end = props.searchParams.startDateEnd ? props.searchParams.startDateEnd : null;
				break;
			case 'targetDate':
				start = props.searchParams.targetDateStart ? props.searchParams.targetDateStart : null;
				end = props.searchParams.targetDateEnd ? props.searchParams.targetDateEnd : null;
				break;
		}

		if (typeof end === 'string') {
			end = Moment.utc(Date.parse(end));
		}
		if (typeof start === 'string') {
			start = Moment.utc(Date.parse(start));
		}

		const newDateRange = {
			...dateRange,
			name: rangeName,
			start: start,
			end: end,
		};
		setShowDateRangePicker(true);
		setDateRange(newDateRange);
	};

	// the event that fires when a new date in the range picker is selected
	// remember these so we can add to search params if they hit the Apply button on the modal
	const onStartRangeChange = (startDate: any, endDate: any) => {
		const newDateRange = {
			...dateRange,
			start: startDate,
			end: endDate,
		};
		setDateRange(newDateRange);
	};

	// the event that fires when the Apply button is clicked in a date range modal
	const onSelectDateRange = async () => {
		setShowDateRangePicker(false);

		// Convert JS Date => Moment => format
		const startAsMoment = dateRange.start ? Moment(dateRange.start) : null;

		const endAsMoment = dateRange.end ? Moment(dateRange.end) : null;

		// One call, not a Redux dispatch followed by updateSearchParams(null, null, true): that
		// second call is the previous render's callback, so it commits the params from before this
		// range was picked and the range never reaches the request.
		// pageNumber resets: the new range can narrow the results to fewer pages than the one
		// currently in view, which would request a page past the end and render an empty table.
		if (dateRange.name === 'startDate') {
			props.onApplySearchParams({
				...props.searchParams,
				pageNumber: 1,
				startDateStart: startAsMoment ? startAsMoment.format(DATE_FORMAT) : null,
				startDateEnd: endAsMoment ? endAsMoment.format(DATE_FORMAT) : null,
			});
		} else if (dateRange.name === 'targetDate') {
			props.onApplySearchParams({
				...props.searchParams,
				pageNumber: 1,
				targetDateStart: startAsMoment ? startAsMoment.format(DATE_FORMAT) : null,
				targetDateEnd: endAsMoment ? endAsMoment.format(DATE_FORMAT) : null,
			});
		}
	};

	const onCancelDateRange = () => {
		setShowDateRangePicker(false);
	};

	const getColumnFilterMultiSelectValues = (sourceIds: any, columnFiltersValues: any) => {
		let results: any[] = [];

		if (!sourceIds || sourceIds.length === 0) return [];

		if (columnFiltersValues && columnFiltersValues.length > 0) {
			for (let i = 0; i < sourceIds.length; i++) {
				const value = columnFiltersValues.find((m: any) => m.id.toString() === sourceIds[i].toString());
				if (value) {
					results.push(value);
				}
			}
		}

		return results;
	};

	const applyBulkAction = () => {
		switch (selectedBulkValue) {
			case 'Inactive':
				props.bulkUpdateInactive();
				resetBulkMultiSelectValue();
				break;
			case 'FollowTask':
			case 'UnfollowTask':
				props.bulkUpdateFollowTask(selectedBulkValue);
				resetBulkMultiSelectValue();
				break;
			case 'ShiftDates':
				props.bulkShiftDates(selectedBulkValue);
				resetBulkMultiSelectValue();
				break;
			default:
				return;
		}
	};

	const handleMultiSelectChange = (key: string) => (e: any) => {
		props.updateSearchParams(key, getMultiSelectFilterValues(e.value), false);
		setDropFilterChanged(true);
	};

	const handleMultiSelectClose = () => {
		if (dropFilterChanged === true) {
			props.updateSearchParams(null, null, true);
			setDropFilterChanged(false);
		}
	};

	const customCloseIcon = () => {
		return (
			<i
				className={classNames('pi', {
					'pi-times': !dropFilterChanged,
					'pi-check-square text-success': dropFilterChanged,
				})}
			/>
		);
	};

	const onCompressionClick = () => {
		setIsCompressionModalOpen(true);
	};

	const onCompressionModalClose = () => {
		setIsCompressionModalOpen(false);
	};

	const renderColumns = () => {
		// because the multiselect needs an object and not just an id
		const departmentValues = getColumnFilterMultiSelectValues(props.searchParams.departmentIds, props.columnFilters.departments);
		const projectValues = getColumnFilterMultiSelectValues(props.searchParams.projectIds, props.columnFilters.projects);
		const communityValues = getColumnFilterMultiSelectValues(props.searchParams.communityIds, props.columnFilters.communities);
		const phaseValues = getColumnFilterMultiSelectValues(props.searchParams.phaseIds, props.columnFilters.phases);
		const statusValues = getColumnFilterMultiSelectValues(props.searchParams.statusIds, props.columnFilters.statuses);
		const tagValues = getColumnFilterMultiSelectValues(props.searchParams.tagIds, props.columnFilters.tags);

		const filters: Record<string, React.ReactNode> = {
			name: (
				<input
					type='text'
					name='name'
					className='p-inputtext'
					onBlur={onSearchBlur}
					onChange={onTextFilterChange}
					onKeyUp={keyUpFilter}
					value={props.searchParams.name}
					placeholder='Search'
				/>
			),
			projectName: (
				<MultiSelect
					value={projectValues}
					onChange={handleMultiSelectChange('projectIds')}
					onHide={handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={props.columnFilters.projects}
					placeholder='Filter'
					closeIcon={customCloseIcon()}
					maxSelectedLabels={1}
				/>
			),
			departmentName: (
				<MultiSelect
					value={departmentValues}
					onChange={handleMultiSelectChange('departmentIds')}
					onHide={handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={props.columnFilters.departments}
					placeholder='Filter'
					closeIcon={customCloseIcon()}
					maxSelectedLabels={1}
				/>
			),
			communityName: (
				<MultiSelect
					value={communityValues}
					onChange={handleMultiSelectChange('communityIds')}
					onHide={handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={props.columnFilters.communities}
					placeholder='Filter'
					closeIcon={customCloseIcon()}
					maxSelectedLabels={1}
				/>
			),
			phaseName: (
				<MultiSelect
					value={phaseValues}
					onChange={handleMultiSelectChange('phaseIds')}
					onHide={handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={props.columnFilters.phases}
					placeholder='Filter'
					closeIcon={customCloseIcon()}
					maxSelectedLabels={1}
				/>
			),
			responsibleUserName: (
				<input
					type='text'
					name='responsibleUserName'
					className='p-inputtext'
					onBlur={onSearchBlur}
					onChange={onTextFilterChange}
					onKeyUp={keyUpFilter}
					value={props.searchParams.responsibleUserName}
					placeholder='Search'
				/>
			),
			startDate: (
				<Button
					type='button'
					bootstrapClass='light'
					name='startDateRangeButton'
					text={props.searchParams.startDateStart || props.searchParams.startDateEnd ? 'Edit Range' : 'Range'}
					onClick={() => openDateRangePicker('startDate')}
				/>
			),
			targetDate: (
				<Button
					type='button'
					bootstrapClass='light'
					name='targetDateRangeButton'
					text={props.searchParams.targetDateStart || props.searchParams.targetDateEnd ? 'Edit Range' : 'Range'}
					onClick={() => openDateRangePicker('targetDate')}
				/>
			),
			additionalInfo: (
				<input
					type='text'
					name='additionalInfo'
					className='p-inputtext'
					onBlur={onSearchBlur}
					onChange={onTextFilterChange}
					onKeyUp={keyUpFilter}
					value={props.searchParams.additionalInfo}
					placeholder='Search'
				/>
			),
			baselineName: (
				<input
					type='text'
					name='baselineName'
					className='p-inputtext'
					onBlur={onSearchBlur}
					onChange={onTextFilterChange}
					onKeyUp={keyUpFilter}
					value={props.searchParams.baselineName}
					placeholder='Search'
				/>
			),
			statusName: (
				<MultiSelect
					value={statusValues.length > 0 ? statusValues : null}
					onChange={handleMultiSelectChange('statusIds')}
					onHide={handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={props.columnFilters.statuses}
					placeholder='Filter'
					closeIcon={customCloseIcon()}
					maxSelectedLabels={1}
				/>
			),
			tags: (
				<MultiSelect
					value={tagValues}
					onChange={handleMultiSelectChange('tagIds')}
					onHide={handleMultiSelectClose}
					optionLabel='name'
					dataKey='id'
					style={{ width: '100%' }}
					options={props.columnFilters.tags}
					placeholder='Filter'
					closeIcon={customCloseIcon()}
					maxSelectedLabels={1}
				/>
			),
		};

		return cols.map((col) => {
			const filterElement = filters[col.field];
			return filterElement ? (
				<Column
					key={col.field}
					sortable={col.isSortable}
					field={col.field}
					filter
					filterElement={filterElement}
					header={col.header}
					body={col.body}
					style={col.style}
				/>
			) : (
				<Column key={col.field} sortable={col.isSortable} field={col.field} header={col.header} body={col.body} style={col.style} />
			);
		});
	};

	const onExport = () => {
		const {
			locationKeys,
			statusIds,
			departmentIds,
			isMilestone,
			phaseIds,
			disciplineIds,
			communityIds,
			targetDateStart,
			targetDateEnd,
			projectIds,
			tagIds,
			isFollowedByCurrentUser,
		} = props.searchParams;

		const urlParams = {
			LocationKeys: locationKeys,
			StatusId: statusIds,
			DepartmentId: departmentIds,
			IsMilestone: isMilestone,
			DisciplineId: disciplineIds,
			PhaseId: phaseIds,
			CommunityId: communityIds,
			TargetDateStart: targetDateStart ? [Moment(targetDateStart).format(DATE_FORMAT).toString()] : null,
			TargetDateEnd: targetDateEnd ? [Moment(targetDateEnd).format(DATE_FORMAT).toString()] : null,
			ProjectId: projectIds,
			TagId: tagIds,
			IsFollowedByCurrentUser: isFollowedByCurrentUser,
		};

		NavigateToRouteWithDashboardParams(Routes.Reports + '/' + REPORT_IDS.TaskList, urlParams, false);
	};

	const onColumnReorder = async (e: any) => {
		try {
			// Extract field names from reordered columns, filtering out null/undefined values
			const newColumnOrder = e.columns
				.map((column: any) => column.props.field)
				.filter((field: any) => field != null);

			// Update local component state with the new column order
			updateLocalColumnState(newColumnOrder);

			// Save the new column order to the backend
			await saveColumnOrderToBackend(newColumnOrder);

		} catch (error) {
			console.error('Error during column reorder:', error);
			Notification('error', 'Error', 'Failed to save column order. Please try again.');
		}
	};

	const updateLocalColumnState = (newColumnOrder: any[]) => {
		const reorderedCols = newColumnOrder
			.map((fieldName) => cols.find((col) => col.field === fieldName))
			.filter((col) => col != null);

		setCols(reorderedCols);
	};

	const saveColumnOrderToBackend = (newColumnOrder: any[]) => {
		props.saveColumnOrder({
			columns: newColumnOrder,
			columnsType: SortColumnsType.TaskListPage
		});
	};

	useEffect(() => {
		document.title = 'Task List';
		buildColumnOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Jump through hoops to make sure we update the list of column options
	 * after we've finished loading the feature flags from the API.  By that
	 * time this component is already mounted and rendered.
	 */
	const isMounted = useRef(false);
	const prevPropsRef = useRef(props);

	useEffect(() => {
		if (!isMounted.current) {
			isMounted.current = true;
			prevPropsRef.current = props;
			return;
		}

		const prevProps = prevPropsRef.current;
		const { isStartDateActive, isTaskDependenciesActive, isTaskStepsActive, isProjectWidgetActive, savedColumns, savedColumnOrder } = props.filters;

		const anyFeaturesChanged =
			isStartDateActive != prevProps.filters.isStartDateActive ||
			isTaskDependenciesActive != prevProps.filters.isTaskDependenciesActive ||
			isTaskStepsActive != prevProps.filters.isTaskStepsActive ||
			isProjectWidgetActive != prevProps.filters.isProjectWidgetActive;

		const savedColumnsChanged = savedColumns != prevProps.filters.savedColumns;
		const savedColumnOrderChanged = savedColumnOrder != prevProps.filters.savedColumnOrder;
		const selectedLocationsChanged = prevProps.searchParams.locationKeys != props.searchParams.locationKeys;
		// Entering/leaving "all locations" mode changes which columns apply, and it can happen with no change to locationKeys
		const allLocationsChanged = isAllLocationsMode(prevProps.searchParams) != isAllLocationsMode(props.searchParams);

		if (anyFeaturesChanged || savedColumnsChanged || savedColumnOrderChanged || selectedLocationsChanged || allLocationsChanged) {
			buildColumnOptions();
		}

		// IE/Edge hack
		// cross browser way of setting the background of the left most column of the datatable based on the status of the task
		const colorElements = document.getElementsByClassName('color-code');
		for (let i = 0; i < colorElements.length; i++) {
			let colorCodeClass;
			const div = colorElements[i].querySelector('div');
			if (div) {
				colorCodeClass = div.classList[0];
			}
			if (colorCodeClass) {
				colorElements[i].classList.add(colorCodeClass);
			}
		}

		prevPropsRef.current = props;
	});

	const masterCheckbox = (
		<div>
			<Checkbox name='checkbox' onChange={() => onMasterCheckboxToggle()} checked={props.allTasksChecked}></Checkbox>
		</div>
	);

	const { sortField, sortOrder } = getDataTableSortFields(props.searchParams);

	const bulkOptions = [
		{ label: 'Change Status', value: 'ChangeStatus' },
		{ label: 'Change Responsible', value: 'ChangeResponsible' },
		{ label: 'Follow Task', value: 'FollowTask' },
		{ label: 'Unfollow Task', value: 'UnfollowTask' },
		{ label: 'Shift Dates', value: 'ShiftDates' },
		{ label: 'Inactive', value: 'Inactive' },
	];

	const { userRole, filters } = props;

	const compressionLocationId = props.locationTasks[0]?.locationId;
	const isSingleLocation = props.searchParams.locationKeys.length === 1;
	const isOwnerOrAdmin = [CUSTOMER_ROLES.Admin.name, CUSTOMER_ROLES.Owner.name].includes(userRole);
	const shouldShowCompressionUtility = isSingleLocation && isOwnerOrAdmin && !!compressionLocationId;

	const shouldShowInactive = [CUSTOMER_ROLES.Admin.name, CUSTOMER_ROLES.Owner.name].includes(userRole);

	let filteredOptions = bulkOptions.filter((o) => {
		if (o.value === 'Inactive' && !shouldShowInactive) return false;
		if (o.value === 'ShiftDates' && !filters.allowLocationTaskDateChanges) return false;
		return true;
	});

	const validBulkActions = ['FollowTask', 'UnfollowTask', 'Inactive', 'ShiftDates'];

	const convertedOptions = filteredOptions.map((option) => {
		const label = typeof option === 'string' ? option : option.label;
		return {
			label,
			value: label.replace(/\s/g, ''),
		};
	});

	const shouldShowApplyButton = checkedTaskCount > 0 && validBulkActions.includes(selectedBulkValue);

	const header = (
		<div className='table-header-container'>
			<div className='row'>
				<div className='col-md-6'>
					<div className='btn-group'>
						<MultiSelectBox
							className='pacer-multi-select bulk-multi-select'
							name='bulkActions'
							options={convertedOptions}
							onChange={onBulkMultiSelectChange}
							idField='value'
							valueField='label'
							placeholder='Bulk Actions'
							isMulti={false}
							selectedValues={selectedBulkValue}
						/>
						{shouldShowApplyButton && (
							<button type='button' className='btn pacer61-purple-btn ms-3' onClick={applyBulkAction}>
								Apply
							</button>
						)}
					</div>
				</div>
				<div className='col-md-6'>
					<div className='d-flex justify-content-end'>
						{shouldShowCompressionUtility && (
							<button
								type='button'
								className='btn pacer61-secondary-btn me-2'
								onClick={onCompressionClick}
								aria-label='Compression Utility'
								title='Compression Utility'>
								<i className='fa-regular fa-clock fa-xl' aria-hidden='true'></i>
							</button>
						)}
						<button
							type='button'
							className='btn pacer61-secondary-btn me-2'
							onClick={props.bulkUpdateByJobTitle}
							{...({ alt: 'Assign tasks by Job Title' } as any)}
							title='Assign tasks by Job Title'>
							<i className='fa-light fa-split fa-xl'></i>
						</button>
						<button
							type='button'
							className='btn pacer61-secondary-btn me-2'
							onClick={onExport}
							{...({ alt: 'Export To PDF' } as any)}
							title='Export To PDF'>
							<i className='fa-regular fa-cloud-arrow-down fa-xl'></i>
						</button>
						<button
							type='button'
							className='btn pacer61-secondary-btn me-2'
							onClick={props.onColumnsClick}
							{...({ alt: 'Choose Columns' } as any)}
							title='Choose Columns'>
							<i className='fa-regular fa-columns-3 fa-xl'></i>
						</button>
					</div>
				</div>
			</div>
		</div>
	);

	const noTasksFound = (
		<div className='d-flex justify-content-center align-items-center'>
			<p className='fs-5'>No tasks found. Click <a style={{ cursor: 'pointer' }} className='link-primary' onClick={props.clearAllFilters}>here</a> to clear all filters.</p>
		</div>
	);

	return (
		<div className='list-view'>
			{(isAllLocationsMode(props.searchParams) || props.searchParams.locationKeys?.length > 0) && (
				<LoadingIndicator isLoading={props.isLoading} isFullscreen={true} hideWhileLoading={true}>
					<Fragment>
						<DataTable
							filterDisplay='row'
							header={header}
							className='p-datatable-sm p-datatable-striped'
							value={props.locationTasks}
							onRowClick={onDataTableRowClick}
							sortField={sortField}
							sortOrder={sortOrder as 1 | 0 | -1}
							onSort={onSort}
							reorderableColumns={true}
							onColReorder={onColumnReorder}
							emptyMessage={noTasksFound}>
							<Column
								className='color-code'
								field='coreStatusId'
								body={colorColumnTemplate}
								style={{ width: '5px', padding: '0px' }}
							/>
							<Column
								field='isChecked'
								filter
								filterElement={masterCheckbox}
								body={checkboxTemplate}
								style={{ textAlign: 'center', width: '40px' }}
							/>
							{renderColumns()}
						</DataTable>
						{props.totalCount > props.searchParams.pageSize && (
							<Paginator
								rows={props.searchParams.pageSize}
								totalRecords={props.totalCount}
								first={(props.searchParams.pageNumber - 1) * props.searchParams.pageSize}
								onPageChange={onPageChange}
							/>
						)}
						<div className='results-count'>Total records: {props.totalCount}</div>
					</Fragment>
				</LoadingIndicator>
			)}

			{isUpdateResponsibleUserDrawerOpen ? (
				<BulkUpdateResponsibleUserDrawer
					responsibleUsers={props.responsibleUsers ?? []}
					userId={props.userId}
					onComplete={onUpdateUserComplete}
					onCancel={closeUpdateUserDrawer}
					locationTasks={[selectedTask]}
					locationKeys={props.searchParams.locationKeys}
				/>
			) : null}
			{showDateRangePicker ? (
				<Modal
					id='startDateRangeModal'
					title='Select Date Range'
					body={
						<div className='datepicker-container'>
							<DateRangePickerWrapper
								onDatesChange={onStartRangeChange}
								initialStartDate={dateRange.start}
								initialEndDate={dateRange.end}
							/>
						</div>
					}
					primaryButtonText='Apply'
					onPrimaryClick={onSelectDateRange}
					onSecondaryClick={onCancelDateRange}
					onCloseClick={onCancelDateRange}
				/>
			) : null}

			{shouldShowCompressionUtility && (
				<TaskCompressionModal
					isOpen={isCompressionModalOpen}
					onClose={onCompressionModalClose}
					locationId={compressionLocationId}
					onCompressionComplete={() => props.updateSearchParams(null, null, true)}
				/>
			)}
		</div>
	);
};

export default memo(ListView);
